import cloneDeep from "lodash.clonedeep";
import {commonEventInfo} from "../fabric-helpers";
import {IEditor, IEditorFontService, Imports, ITextZoneProperties, ITextOptions} from "../editor.interfaces";



/* tslint:disable no-invalid-this */

const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

const MIN_FONT_SIZE = 6;

const GRAB_HANDLE_MIDDLE_RIGHT = 'mr';
const GRAB_HANDLE_MIDDLE_LEFT = 'ml';
const GRAB_HANDLE_MIDDLE_TOP = 'mt';
const GRAB_HANDLE_MIDDLE_BOTTOM = 'mb';

const fabric = globalImports.fabric || (globalImports.fabric = {});
const SF = globalImports.SF || {};
SF.Editor = SF.Editor || {} as IEditor;
SF.Util = SF.Util || globalImports.SF.Util;
globalImports.SF.Editor = globalImports.SF.Editor || {};

// noinspection JSUnusedLocalSymbols
globalImports.SF.Editor.Text = fabric.util.createClass(fabric.Textbox, {
    type: 'sf-text',
    //stateProperties: stateProperties,

    // the flash editor scales text by a factor of 5, for no reason, so we do it to..
    // @note: changing the font size and scaling are not equivalent, as scaling is different on large font sizes.
    scaleX: 5,
    scaleY: 5,
    fill: 'rgba(0,0,0,1)',

    ready: false,       // if font isn't loaded yet, don't render
    loadingFont: 0,     // if a font is loading, also don't render
    failedFire: 0,
    minFontSize: 6,
    maxFontSize: 500,
    //only used on the server because we don't have the font service there
    openedFont: null,
    adjustedTextSize: null,

    initialize: function (text: string, options: ITextOptions, keepEmptyText: boolean = false): void {

        // adjust the width and height, so that we scale it down by 5
        if (options.width) {
            options.width /= this.scaleX;
        }

        if (options.height) {
            options.height /= this.scaleY;
        }

        if (!options.effects) {
            options.effects = {};
        }

        // if we are in node
        if (fabric.isLikelyNode) {
            // font is always ready
            this.ready = true;
        }

        // Handles case-sensitive differences in DefaultText among all environments

        if (keepEmptyText) {
            text = '';
        } else if (!fabric.isLikelyNode && (!text || text.toLowerCase() == (globalImports.SF.Model.DefaultText['old_default']).toLowerCase())) {
            text = globalImports.SF.Model.DefaultText['large'];
        }

        text = this.fixTextFromOptions(text);
        options.originalFontSize = options.fontSize;

        this.callSuper('initialize', text, options);

        // Keep track of edited default text for the sake of text-edit mode

        const messageOptions: string[] = [];
        const language = this.getCurrentLanguage();
        if (options.placeHolderTranslations && options.placeHolderTranslations[language]) {
            messageOptions.push(options.placeHolderTranslations[language]);
        } else {
            for (const key in globalImports.SF.Model.DefaultText) {
                if (globalImports.SF.Model.DefaultText.hasOwnProperty(key)) {
                    messageOptions.push(((globalImports.SF.Model.DefaultText) as  { [key: string]: any })[key]);
                }
            }
        }
        if (!options.originalHeight) {
            this.originalHeight = options.height;
        }
        this.textZoneId = options.textZoneId;
        this.linkedZoneId = options.linkedZoneId;
        this.autoFontSize = !!options.autoFontSize;
        this.setAdjustedTextSize(text, options, messageOptions);
        this.forceClearCache();
        this.setControlsVisibility({mr: true, ml: true});

    },

    autoFontSizeAdjust: function():boolean {
        if (!this.originalFontSize && this.fontSize) {
            this.originalFontSize = this.fontSize;
        }
        if (!this.originalWidth && this.width) {
            this.originalWidth = this.width;
        }
        if (!this.textBeforeEdit) {
            this.textBeforeEdit = this.text;
        }

        let changed = false;
        let count = 0;  // This can probably be removed, it was here as a safety to stop from hanging the browser while I developed the functionality.

        if (this.fontSize < this.originalFontSize) {
            while (this._textLines.length === 1 && this.fontSize < (this.originalFontSize as number) && count++ < 100) {
                this.setStyle('fontSize', (this.fontSize as number) + 1);
                changed = true;
            }
        }

        if (this._textLines.length === 1 && this.width && this.originalWidth && this.width > this.originalWidth) {
            if (this.fontSize > MIN_FONT_SIZE) {
                do {
                    this.setStyle('fontSize', (this.fontSize as number) - 1);
                } while (this._measureLine(0).width > this.originalWidth && this.fontSize > MIN_FONT_SIZE);
                changed = true;
            } else {
                this.set({this: this.textBeforeEdit});
            }
            this.width = this.originalWidth;
        }

        while (this._textLines.length > 1  && count++ < 100) {
            if (this.fontSize > MIN_FONT_SIZE) {
                this.setStyle('fontSize', (this.fontSize as number) - 1);
                changed = true;
            } else {
                this.set({this: this.textBeforeEdit});
            }
        }

        return changed;
    },

    setAdjustedTextSize: function (text: string, options: ITextOptions, messageOptions: string[]): void {
        const language = this.getCurrentLanguage();
        if ((text == globalImports.SF.Model.DefaultText['large'] ||
            (options.placeHolderTranslations && text == options.placeHolderTranslations[language])) && options.edited === false) {
            this.text = this._truncateText(options, messageOptions);
            if (options.placeHolderTranslations) {
                this.adjustedTextSize = null;
            } else {
                this.adjustedTextSize = this._getDefaultTextKey(this.text).toString();
            }
            if (this.addedByUser) {
                this.text = '';
            }
        }
    },

    forceClearCache: function(): void {
        // do nothing -- override if needed
    },

    _truncateText: function (options: any, messageOptions: string[]): string {
        let virtualCanvas: any;

        if (fabric.isLikelyNode) {
            // noinspection JSPotentiallyInvalidConstructorUsage
            virtualCanvas = new (global as any).canvas.createCanvas(4000, 4000);
        } else {
            virtualCanvas = document.createElement('canvas');
            virtualCanvas.width = 4000;
            virtualCanvas.height = 4000;
        }

        const ctx = virtualCanvas.getContext('2d');
        this._setTextStyles(ctx);

        const textZoneProperties: ITextZoneProperties = {
            maxWidth: options.width,
            lineHeight: this.height,
            zoneHeight: this.originalHeight ? this.originalHeight * this.scaleY : options.height * this.scaleY,
            measureText: this._measureWord.bind(this)
        };

        // Assigning newText as 'small' variation of DefaultText in case upcoming loop fails to match

        if (messageOptions.length > 1) {
            let newText = messageOptions[messageOptions.length - 2];

            // Returning true breaks the 'some' loop once if statement passes

            messageOptions.some((sentence: string) => {
                if (this._simulateWrappedText(sentence, textZoneProperties)) {
                    newText = sentence;
                    return true;
                }
            });

            return newText;
        } else {
            return messageOptions[0];
        }
    },

    /**
     *  Method that simulates wrapping the text in an imaginary canvas to determine if the sentence
     *  would fit in the given textZone
     *  @param message
     *  @param {Object} textZoneProperties Object that contains properties of text and zone
     *  @return {boolean} True if the fully-wrapped text fits within textZone height
     */
    _simulateWrappedText: function (message: string, textZoneProperties: ITextZoneProperties): boolean {
        let line = '';
        const splitText = message.split('');
        const lastIndex = splitText.length - 1;
        let virtualTextBoxHeight = textZoneProperties.lineHeight;

        splitText.forEach((word, index) => {
            const testLine = line + word + ' ';
            const testWidth = textZoneProperties.measureText(testLine, 0);
            if (testWidth > textZoneProperties.maxWidth) {
                line = this._isEndOfSentence(index, lastIndex) ? word : word + ' ';
                virtualTextBoxHeight += textZoneProperties.lineHeight;
            } else {
                line = testLine;
            }
        });

        return virtualTextBoxHeight <= textZoneProperties.zoneHeight;

    },

    _renderText: function (ctx: CanvasRenderingContext2D):void {
        if (this.paintFirst === 'stroke') {
            this._renderTextStroke(ctx);
            this._renderTextFill(ctx);
        } else {
            this._setShadow(ctx);
            this._renderTextFill(ctx);
            this._renderTextStroke(ctx);
        }
    },

    _shouldClearDimensionCache: function (): boolean {
        let shouldClear = this._forceClearCache;
        if (!shouldClear) {
            shouldClear = this.hasStateChanged('_dimensionAffectingProps');
        }
        if (shouldClear) {
            this.dirty = true;
        }
        return shouldClear;
    },

    getCanvasWithTextOnly: function (textObject: fabric.Object): HTMLCanvasElement {
        const virtual = document.getElementById('virtualTextOnly');
        if (virtual) {
            virtual.remove();
        }
        const virtualCanvas = document.createElement('canvas');
        virtualCanvas.id = 'virtualTextOnly';
        virtualCanvas.style.display = 'none';
        document.body.appendChild(virtualCanvas);
        const fabricCanvas = new fabric.Canvas('virtualTextOnly');
        (fabricCanvas as any).fontService = (textObject as any).canvas.fontService;
        // noinspection JSVoidFunctionReturnValueUsed -- Currently has bad ts data. Clone does return an object.
        const clonedText = fabric.util.object.clone(textObject);
        fabricCanvas.backgroundColor = 'rgba(0,0,0,0)';
        clonedText.scaleX = textObject.scaleX;// / devicePixelRatio;
        clonedText.scaleY = textObject.scaleY;// / devicePixelRatio;
        fabricCanvas.viewportTransform = cloneDeep(clonedText.canvas.viewportTransform);
        fabricCanvas.vptCoords = cloneDeep(clonedText.canvas.vptCoords);
        fabricCanvas.width = clonedText.canvas.width;
        fabricCanvas.height = clonedText.canvas.height;
        fabricCanvas.lowerCanvasEl.width = clonedText.canvas.width * fabric.devicePixelRatio;
        fabricCanvas.lowerCanvasEl.height = clonedText.canvas.height * fabric.devicePixelRatio;
        fabricCanvas.add(clonedText);
        fabricCanvas.renderAll();
        return fabricCanvas.lowerCanvasEl;
    },

    getAvgRGB: function (imageData: Uint8ClampedArray): { r: number, g: number, b: number } {

        let count = 0;
        const rgb = {r: 0, g: 0, b: 0};
        let rgbArray: number[] = [];
        for (let i = 0; i < imageData.length; i += 4) {
            const alpha = imageData[i + 3] / 255;
            const r = imageData[i] / 255;
            const g = imageData[i + 1] / 255;
            const b = imageData[i + 2] / 255;
            const rgbColor = {
                r: ((1 - alpha) + alpha * r) * 255,
                g: ((1 - alpha) + alpha * g) * 255,
                b: ((1 - alpha) + alpha * b) * 255
            };
            rgbArray = rgbArray.concat(Object.keys(rgbColor).map(key => (rgbColor as any)[key]));

        }

        for (let i = 0; i < rgbArray.length; i += 3) {
            ++count;
            rgb.r += Math.pow(rgbArray[i], 2);
            rgb.g += Math.pow(rgbArray[i + 1], 2);
            rgb.b += Math.pow(rgbArray[i + 2], 2);
        }

        rgb.r = Math.sqrt(rgb.r / count);
        rgb.g = Math.sqrt(rgb.g / count);
        rgb.b = Math.sqrt(rgb.b / count);

        return rgb;
    },

    findBackgroundColor: function (): { r: number, g: number, b: number } {

        const canvas = document.createElement('canvas');
        canvas.className = 'backgroundColorCanvas';
        const boundingRect = this.getBoundingRect();
        const devicePixelRatio = fabric.devicePixelRatio;
        canvas.width = boundingRect.width;
        canvas.height = boundingRect.height;
        const ctx = canvas.getContext('2d');
        (ctx as CanvasRenderingContext2D).drawImage(this.canvas.lowerCanvasEl, boundingRect.left * devicePixelRatio,
            boundingRect.top * devicePixelRatio, boundingRect.width * devicePixelRatio, boundingRect.height * devicePixelRatio,
            0, 0, canvas.width, canvas.height);
        const textOnlyCanvas = this.getCanvasWithTextOnly(this);
        const textOnlyCanvasCtx = textOnlyCanvas.getContext('2d');
        const originalComposition = (textOnlyCanvasCtx as CanvasRenderingContext2D).globalCompositeOperation;
        (textOnlyCanvasCtx as CanvasRenderingContext2D).globalCompositeOperation = 'source-out';
        (textOnlyCanvasCtx as CanvasRenderingContext2D).drawImage((ctx as CanvasRenderingContext2D).canvas, 0, 0, canvas.width, canvas.height, boundingRect.left, boundingRect.top, canvas.width, canvas.height);
        (textOnlyCanvasCtx as CanvasRenderingContext2D).globalCompositeOperation = originalComposition;
        const onePixelCanvas = document.createElement('canvas');
        onePixelCanvas.className = 'onePixelCanvas';
        const onePixelCanvasSize = devicePixelRatio >= 1 ? devicePixelRatio : 1;
        onePixelCanvas.width = onePixelCanvasSize;
        onePixelCanvas.height = onePixelCanvasSize;
        const onePixelCanvasCtx = onePixelCanvas.getContext('2d');
        (onePixelCanvasCtx as CanvasRenderingContext2D).clearRect(0, 0, onePixelCanvas.width, onePixelCanvas.height);
        (onePixelCanvasCtx as CanvasRenderingContext2D).drawImage(
            (textOnlyCanvasCtx as CanvasRenderingContext2D).canvas, boundingRect.left, boundingRect.top, boundingRect.width, boundingRect.height, 0, 0, onePixelCanvas.width, onePixelCanvas.height);

        const imageData = (onePixelCanvasCtx as CanvasRenderingContext2D).getImageData(0, 0, onePixelCanvas.width, onePixelCanvas.height);
        let rgb: { r: number, g: number, b: number };
        if (devicePixelRatio > 1) {
            rgb = this.getAvgRGB(imageData.data);
        } else {
            const alpha = imageData.data[3] / 255;
            const r = imageData.data[0] / 255;
            const g = imageData.data[1] / 255;
            const b = imageData.data[2] / 255;
            rgb = {
                r: ((1 - alpha) + alpha * r) * 255,
                g: ((1 - alpha) + alpha * g) * 255,
                b: ((1 - alpha) + alpha * b) * 255
            };

        }

        const backgroundColorCanvases = document.getElementsByClassName('backgroundColorCanvas');
        if (backgroundColorCanvases.length > 0) {
            const backgroundColorCanvas = backgroundColorCanvases[0] as HTMLCanvasElement;
            backgroundColorCanvas.width = 0;
            backgroundColorCanvas.height = 0;
            backgroundColorCanvas.remove();
        }
        const onePixelCanvases = document.getElementsByClassName('onePixelCanvas');
        if (onePixelCanvases.length > 0) {
            const canvasToDelete = backgroundColorCanvases[0] as HTMLCanvasElement;
            canvasToDelete.width = 0;
            canvasToDelete.height = 0;
            canvasToDelete.remove();
        }
        this.removeTextOnlyCanvas();
        return rgb;
    },

    removeTextOnlyCanvas: function (): void {
        const virtual = document.getElementById('virtualTextOnly');
        if (virtual && virtual.parentElement) {
            virtual.parentElement.remove();
        }
    },

    setReady: function (): void {
        this._forceClearCache = true;
        this.ready = true;
        this.fire('ready');
    },

    fireOnObjectReady: function(): void {
        // Note: do not include the following lines in setReady function...
        if (this.canvas && typeof this.canvas._onObjectReady === 'function' && this.loadingFont == 0) {
            this.canvas._onObjectReady({target: this});
        }
    },

    // /**
    //  * @todo: delete after https://github.com/kangax/fabric.js/issues/2850 is resolved
    //  */
    // renderCursorOrSelection: function():void {
    //     this.callSuper('renderCursorOrSelection');
    //     this.canvas.fire('after:render');
    // },

    triggerModifiedEvent: function (): void {
        if (this.canvas._fire) {
            this.canvas._fire('modified', commonEventInfo(null, this,  true));
        }
    },

    fixTextFromOptions: function (text:string): string {
        // Remove non-ascii characters, note: this will not allow non-latin characters and may have to be made more robust in the future.

        text = text.replace(/[^\x00-\xff]/g, '');
        if (this.maxCharacters > 0 && text.length > this.maxCharacters) {
            text = text.substring(0, this.maxCharacters);
        }
        if (this.textTransform === 'uppercase') {
            text = text.toUpperCase();
        } else if (this.textTransform === 'lowercase') {
            text = text.toLowerCase();
        } else if (this.textTransform === 'capitalize') {
            text = text.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
        } else if (this.textTransform === 'numeric') {
            text = text.replace(/[^\d]+/g, '');
        }

        return text;
    },

    onInput: function (e: any): void {
        this.hiddenTextarea.value = this.fixTextFromOptions(this.hiddenTextarea.value);
        this.callSuper('onInput', e);
    },

    /**
     * If we are loading a new object, and the font hasn't loaded, don't render it.
     *
     * @param ctx
     * @param noTransform
     */
    render: function (ctx: CanvasRenderingContext2D, noTransform: boolean): void {
        if (this.loadingFont !== 0) {
            return;
        }
        // if we aren't ready yet, and have access to a font service
        if (!this.ready && this.canvas.fontService) {
            if (this.loadingFont === 0) {
                const fonts = SF.Util.Fonts.fromShape(this);
                if (!this.canvas.fontService.areLoaded(fonts)) {
                    this.loadingFont++;
                    this.canvas.fontService
                        .loadMultiple(fonts)
                        .then((): void => {
                            this.loadingFont--;
                            this.triggerModifiedEvent();
                            fabric.util.clearFabricFontCache(this.fontFamily);
                            this.setReady();
                            this.canvas.renderAll();
                            this.fireOnObjectReady();
                        })
                        .catch((err: string) => {
                            console.log('Error loading fonts', err);
                            this.replaceFailedFont().then((defaultFont: string): void => {
                                this.replaceDefaultFontInDropDown(defaultFont);
                            });
                        });
                    this.callSuper('render', ctx, noTransform);
                    return;
                } else {
                    this.triggerModifiedEvent();
                    this.setReady();
                }
            } else {
                return;
            }
        }

        this.callSuper('render', ctx, noTransform);
    },

    toObject: function (propertiesToInclude: any): Object {
        return fabric.util.object.extend(this.callSuper('toObject', propertiesToInclude), {
            locked: this.locked,
            width: this.width * this.scaleX,
            height: this.height * this.scaleY,
            left: this.left,
            effects: this.effects ? fabric.util.object.clone(this.effects) : null,
        });
    },

    setStyle: function (styleName: string, value: any): void {

        if (styleName == 'fontSize') {
            if (value < this.minFontSize || value > this.maxFontSize) {
                return;
            }
        }

        if (styleName === 'fontFamily' || styleName === 'fontWeight' || styleName === 'fontStyle') {
            const fontService:IEditorFontService = this.canvas.fontService;
            const fontFamily = styleName === 'fontFamily' ? value : this.getStyle('fontFamily');
            const font = fontService.get(fontFamily);

            if (!font) {
                console.warn('Unable to find font ' + fontFamily);
                return;
            }

            const fontWeight = styleName === 'fontWeight' ? value : this.getStyle('fontWeight');
            const fontStyle = styleName === 'fontStyle' ? value : this.getStyle('fontStyle');
            let styleProp = SF.Util.Fonts.getStyleFromProperties(fontWeight === 'bold', fontStyle === 'italic');

            // the user may be switching to a font family that may not have the italic or bold face
            if (styleName === 'fontFamily' && !font.hasStyle(styleProp)) {
                // switch it to first available style
                styleProp = font.styles[0];

                if (fontWeight === 'bold') {
                    this.set('fontWeight', styleProp === 'b' || styleProp === 'bi' ? 'bold' : 'normal');
                }

                if (fontStyle === 'italic') {
                    this.set('fontStyle', styleProp === 'i' || styleProp === 'bi' ? 'italic' : 'normal');
                }
            }

            if (!fontService.isLoaded(fontFamily, styleProp)) {
                this.loadingFont++;
                if (this.isRegularStyleLoaded(styleName, font, fontFamily)) {
                    this.loadingFont--;
                }

                fontService
                    .load(font, styleProp)
                    .then((): void => {
                        // clear font cache
                        this._forceClearCache = true;

                        // mark not loading
                        if (this.loadingFont > 0) {
                            this.loadingFont--;
                            this.triggerModifiedEvent();
                        }
                        // re-render
                        this.canvas.renderAll();
                        fabric.util.clearFabricFontCache(this.fontFamily);
                        this.fireOnObjectReady();
                    })
                    .catch((e:string): void => {
                        console.error(`Error loading font ${font ? font.name : 'EMPTY'} style ${styleProp}`, e);
                        this.replaceFailedFont(this).then((defaultFont: string): void => {
                            this._forceClearCache = true;
                            this.replaceDefaultFontInDropDown(defaultFont);
                            this.canvas.renderAll();
                        });
                    });
            }
        }

        // if we are editing, apply the style to the selection
        if (this.isEditing && this.selectionStart !== this.selectionEnd && styleName !== 'textAlign' && !(this.selectionStart === 0 && this.selectionEnd === this.text.length)) {
            const style: any = {};
            style[styleName] = value;
            this.setSelectionStyles(style);

            return;
        }

        // do not apply fill color if it's empty
        if (styleName === 'fill' && !value) {
            value = '#000000';
        }
        const styles: any = {};
        styles[styleName] = value;
        // apply the style to the whole object
        this.setSelectionStyles(styles, 0, this.text.length);
        this.set(styleName, value);
    },

    isRegularStyleLoaded: function(styleName: string, font: any, fontFamily: any): boolean {
        return false;
    },

    getStyle: function (styleName: string): any {
        if (this.isEditing) {
            const styles = this.getSelectionStyles();
            if (styles.length > 0 && (styleName === 'fontWeight' || styleName === 'fontStyle' || styleName === 'underline')) {
                return styles[0][styleName];
            } else if (styles.hasOwnProperty(styleName)) {
                return styles[styleName];
            }
        }

        return this[styleName];
    },

    toUpperCase: function (): void {
        if (this.isEditing) {
            this.text = this.text.substring(0, this.selectionStart) +
                this._toUpperCase(this.text.slice(this.selectionStart, this.selectionEnd)) +
                this.text.slice(this.selectionEnd);
        } else {
            this.text = this._toUpperCase(this.text);
        }
    },

    _toUpperCase: function (selection: string): string {
        return selection[selection === selection.toUpperCase() ? 'toLowerCase' : 'toUpperCase']();
    },

    enterEditing: function (args: any): void {
        if (!this.isEditing) {
            if (!args || !args.skipParentEnterEditing) {
                this.edited = true;
                this.textBeforeEdit = this.text;
                if (this.adjustedTextSize && this.text == ((globalImports.SF.Model.DefaultText) as { [key: string]: any })[this.adjustedTextSize] || this._isADefaultText(this.text) ||
                    (this.placeHolderTranslations && this.text == this.placeHolderTranslations[this.getCurrentLanguage()])) {
                    this.selectAll();
                    this.removeChars(0, this.text.length);
                    this.setSelectionStart(0);
                    this.setSelectionEnd(0);
                    this._forceClearCache = true;
                    if (this.addedByUser) {
                        this.textBeforeEdit = this.text;
                    }
                    this.canvas.renderAll();
                }
            }
            window.SFLanguage
            window.isEnteringText = true;
            this.callSuper('enterEditing');
            window.isEnteringText = false;
        }
    },

    exitEditing: function (skipEmptyToDefault:boolean = false): void {
        if (!skipEmptyToDefault && this.text.trim() === '') {
            const defaultText = globalImports.SF.Model.DefaultText as { [key: string]: any };
            if (!this.addedByUser) {
                this.edited = false;
                if (!this.placeHolderTranslations) {
                    if (this.adjustedTextSize) {
                        this.text = defaultText[this.adjustedTextSize];
                    } else {
                        this.text = defaultText['small'];
                    }
                } else {
                    this.text = this.placeHolderTranslations[this.getCurrentLanguage()];
                }
            } else {
                this.textBeforeEdit = this.text;
                this.text = this.adjustedTextSize ? defaultText[this.adjustedTextSize] : defaultText['small'];
            }
        }

        this.callSuper('exitEditing');
    },

    /**
     * Override and convert to no-op. Will be removed after my next PR to fabric.js
     */
    setOnGroup: function (): void { /* */
    },

    getCurrentLanguage: function (): string {
        return SF.currentLanguage;
    },

    _findTargetCorner: function (pointer: any): any {
        if (!this.hasControls || !this.selected) {
            return false;
        }

        const ex = pointer.x;
        const ey = pointer.y;
        let xPoints;
        let lines;
        this.__corner = 0;
        const padding = 5;
        for (const coordinate in this.oCoords) {
            if (this.oCoords.hasOwnProperty(coordinate)) {

                if (!this.isControlVisible(coordinate)) {
                    continue;
                }

                if (coordinate === 'mtr' && !this.controls.mtr) {
                    continue;
                }

                if (this.get('lockUniScaling') &&
                    (coordinate === GRAB_HANDLE_MIDDLE_TOP ||
                        coordinate === GRAB_HANDLE_MIDDLE_RIGHT ||
                        coordinate === GRAB_HANDLE_MIDDLE_BOTTOM ||
                        coordinate === GRAB_HANDLE_MIDDLE_LEFT)) {
                    continue;
                }

                lines = this.adjustLines(this._getImageLines(this.oCoords[coordinate].corner), coordinate, padding);

                xPoints = this._findCrossPoints({x: ex, y: ey}, lines);
                if (xPoints !== 0 && xPoints % 2 === 1) {
                    this.__corner = coordinate;
                    return coordinate;
                }
            }
        }
        return false;

    },

    // should be overridden by mobile and kiosk
    adjustLines: function (lines: any, coordinate: string, padding: number): any {
        return lines;
    },

    replaceDefaultFontInDropDown: function (defaultFont: string): void {
        console.warn('Replace the default font in the dropdown, not implemented: ' + defaultFont);
    },

    replaceFailedFont: function (): Promise<string> {
        return new Promise<string>((resolve: (font: string) => void) => {
            this.canvas.fontService.getFirstLoadedFont().then((defaultFont: string) => {
                this.loadingFont--;
                this.fontFamily = defaultFont;
                this.setStyle('fontFamily', defaultFont);
                this.setReady();
                if (typeof this.canvas._fire == 'function') {
                    if (this.failedFire++ < 5) {
                        this.canvas._fire('modified', commonEventInfo(null, this,  true));
                    }
                }
                resolve(defaultFont);
            });
        });
    },

    _isEndOfSentence: function (index: number, lastIndex: number): boolean {
        return index == lastIndex;
    },

    _getDefaultTextKey: function (text: string): string | undefined {
        const defaultText = globalImports.SF.Model.DefaultText as { [key: string]: any };
        for (const key in globalImports.SF.Model.DefaultText) {
            if (globalImports.SF.Model.DefaultText.hasOwnProperty(key) && defaultText[key] == text) {
                return key;
            }
        }
    },

    _isADefaultText: function (text: string): boolean {
        const defaultText = globalImports.SF.Model.DefaultText as { [key: string]: any };
        const sizes = Object.keys(defaultText);

        return sizes.some((size: string) => {
            return defaultText[size] === text;
        });
    },

    isDoubleClick: function (newPointer: any): boolean {
        return !this.fixed
            && this.__newClickTime - this.__lastClickTime < 500
            && this.__lastPointer.x === newPointer.x
            && this.__lastPointer.y === newPointer.y
            && this.__lastIsEditing;

    },

});